import { useActions, useAgentParams, usePatient } from '@/store/selectors';
import { Agent, Indication } from '@/types';
import { INDICATION_TARGETS } from '@/config/indications';
import { indicationLabel } from '@/utils/format';
import { Select } from 'calc-controls';

/** Agent + indication selection → loads params + targets (PSD §8.1.2 / TODO Task 1.21, 2.17). */
export function DrugIndicationSelector(): JSX.Element {
  const actions = useActions();
  const { patient } = usePatient();
  const agentParams = useAgentParams();
  const indication = patient?.indication ?? Indication.GramNegativeSepsis;
  const target = INDICATION_TARGETS[indication];

  const isAmikacin = agentParams.agent === Agent.Amikacin;
  const peakRange = isAmikacin
    ? agentParams.targetPeakRange.map((v) => v / 3)
    : agentParams.targetPeakRange;

  return (
    <section className="panel" aria-label="Drug and indication" data-testid="drug-indication">
      <h2 className="panel-title">Drug &amp; Indication</h2>
      <div data-testid="field-agent">
        <Select
          label="Agent"
          tooltip="Selects the aminoglycoside. Dosing targets, ranges, and rounding increments are agent-specific."
          options={[
            { value: Agent.Gentamicin, label: 'Gentamicin' },
            { value: Agent.Tobramycin, label: 'Tobramycin' },
            { value: Agent.Amikacin, label: 'Amikacin' },
            { value: Agent.Plazomicin, label: 'Plazomicin' },
          ]}
          value={agentParams.agent}
          onChange={(v) => actions.setAgent(v as Agent)}
        />
      </div>
      <div data-testid="field-drug-indication">
        <Select
          label="Indication"
          tooltip="Sets target peak/trough ranges and dose intensity multiplier for the selected indication."
          options={Object.values(Indication).map((ind) => ({ value: ind, label: indicationLabel(ind) }))}
          value={indication}
          onChange={(v) => actions.setIndication(v as Indication)}
        />
      </div>
      <div className="value-row" title="Extended-interval mg/kg dose target for the selected agent.">
        <span className="value-label">EI dose target</span>
        <span className="value-num">
          {agentParams.eiDoseMgKg} mg/kg
        </span>
      </div>
      <div className="value-row">
        <span className="value-label">Target peak range</span>
        <span className="value-num">
          {peakRange[0]}–{peakRange[1]} mg/L
        </span>
      </div>
      <div className="value-row">
        <span className="value-label">Target trough range</span>
        <span className="value-num">
          {agentParams.targetTroughRange[0]}–{agentParams.targetTroughRange[1]} mg/L
        </span>
      </div>
      <div className="value-row" title="Dose intensity multiplier applied to the agent's base mg/kg for the selected indication.">
        <span className="value-label">Indication intensity</span>
        <span className="value-num">{target.intensityMultiplier}×</span>
      </div>
    </section>
  );
}
